import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { tap, timeout, finalize, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Search Request Interface
export interface SearchRequest {
  currency?: string;
  checkInDate?: string;
  checkOutDate?: string;
  countryCode?: string;
  destinations?: SearchDestination[];
  pageNumber?: number;
  pageSize?: number;
  rooms?: SearchRoom[];
  includeRoomPrices?: boolean;
  includeDetails?: number[];
  requestKey?: string; // Pagination için RequestKey
  searchFilterItem?: SearchFilterItem; // GetFilters ile seçilen filtreler
  orderBy?: 'Average' | 'PriceAscending' | 'PriceDescending';
}

// Gordios GetFilters kodlarıyla server-side filtreleme
export interface SearchFilterItem {
  filterPriceRanges?: FilterPriceRange;
  filterAvailableTypeCode?: number | null; // null=hepsi, 0=uygun, 1=uygun değil
  filterHotelName?: string; // "contains" eşleşmesi
  filterLocationIds?: number[];
  filterCategoryTypeCodes?: number[];
  filterCampaignTypeCodes?: string[];
  filterDiscountTypeCodes?: string[];
  filterFoodAndDrinkCodes?: string[];
  filterHotelFacilityCodes?: string[];
  filterHotelRoomFacilityCodes?: string[];
  filterHotelThemeCodes?: string[];
  filterHotelTypeCodes?: string[];
  filterHygienicalMeasureCodes?: string[];
  filterMealTypeCodes?: string[];
  filterNearDistanceCodes?: string[];
  filterActionTypeCodes?: string[];
}

export interface FilterPriceRange {
  minPrice?: number | null;
  maxPrice?: number | null;
}

// Kullanıcı arama kriterleri (Form verileri)
export interface UserSearchCriteria {
  location: string;
  selectedLocation: any; // Autocomplete'den seçilen lokasyon objesi
  checkInDate: Date | null; // Tarihsiz aramalar için null olabilir
  checkOutDate: Date | null; // Tarihsiz aramalar için null olabilir
  adults: number;
  children: number;
  rooms: number;
  room2Adults: number;
  room2Children: number;
  childrenAges: number[];
  room2ChildrenAges: number[];
}

export interface SearchDestination {
  destinationId?: number;
  hotelId?: number;
  destinationLabel: string;
  destinationParentId?: number;
  locationType: number;
}

export interface SearchRoom {
  passengers: SearchPassenger[];
}

export interface SearchPassenger {
  age: number;
  birthday: string | null;
  gender: number | null;
  passengerType: number;
}

// Search Response Interface
export interface SearchResponse {
  success: boolean;
  traceId?: string;
  requestKey?: string; // Sonraki sayfalar için kullanılacak
  pageSize?: number;
  pageNumber?: number;
  totalCount?: number;
  items?: any[];
  hotels?: { [key: string]: any };
  passengers?: { [key: string]: any };
  searchCriteria?: {
    checkInDate?: string;
    checkOutDate?: string;
    currency?: string;
    countryCode?: string;
    destinations?: any[];
    rooms?: any[];
    passengers?: any[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private searchResults = new BehaviorSubject<SearchResponse | null>(null);
  searchResults$ = this.searchResults.asObservable();

  private _isSearching = new BehaviorSubject<boolean>(false);
  isSearching$ = this._isSearching.asObservable();
  private apiUrl = environment.apiUrl;
  private passengerKeys: string[] = []; // Search'ten gelen passenger key'leri

  // Search cache + in-flight dedup (aynı parametreyle 60 sn içinde tekrar istek atma)
  private searchCache = new Map<string, { obs: Observable<SearchResponse>; ts: number }>();
  private readonly SEARCH_CACHE_TTL = 60_000;
  
  // Pagination için saklanacak değerler
  private currentRequestKey: string | null = null;
  private lastSearchParams: SearchRequest | null = null;
  private currentPageNumber = 1;
  private totalCount = 0;
  private pageSize = 10;
  
  // Kullanıcı arama kriterleri (form verileri için)
  private userSearchCriteria: UserSearchCriteria | null = null;
  
  // Booking özet verileri (hotel-detail'den passenger-info'ya)
  private addonData: { addons: any[]; basePrice: any; totalPrice: any; productKey: string; traceId: string; passengerKeys: string[] } | null = null;
  private bookingSummary: { hotelName: string; hotelImage: string; hotelStars: number; roomType: string; mealType: string; checkIn: string; checkOut: string; adultCount: number; childCount: number; roomCount: number } | null = null;

  // Otel önizleme (hotel-detail anında gösterim için)
  private selectedHotelPreview: any = null;

  setSelectedHotelPreview(hotel: any): void {
    this.selectedHotelPreview = hotel;
  }

  getSelectedHotelPreview(): any {
    return this.selectedHotelPreview;
  }

  // ⭐ Minimum gece kısıtlaması için alternativeRoomGroup bilgisi
  private alternativeRoomGroupData: any[] | null = null;
  private minNightRestrictionInfo: { hasRestriction: boolean; minNight: number; message: string } = {
    hasRestriction: false,
    minNight: 0,
    message: ''
  };

  // SessionStorage keys
  private readonly STORAGE_KEYS = {
    SEARCH_RESULTS: 'pusula_search_results',
    REQUEST_KEY: 'pusula_request_key',
    LAST_SEARCH_PARAMS: 'pusula_last_search_params',
    PAGINATION: 'pusula_pagination',
    USER_CRITERIA: 'pusula_user_criteria',
    PASSENGER_KEYS: 'pusula_passenger_keys',
    TIMESTAMP: 'pusula_data_timestamp'
  };

  // Cache süresi (2 dakika = 120000 ms)
  private readonly CACHE_DURATION = 2 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadFromStorage();
  }

  /**
   * SessionStorage'dan verileri yükler
   */
  private loadFromStorage(): void {
    try {
      // Önce timestamp kontrolü yap
      const storedTimestamp = sessionStorage.getItem(this.STORAGE_KEYS.TIMESTAMP);
      const isDataFresh = storedTimestamp && (Date.now() - parseInt(storedTimestamp)) < this.CACHE_DURATION;

      // Request key
      const storedRequestKey = sessionStorage.getItem(this.STORAGE_KEYS.REQUEST_KEY);
      if (storedRequestKey) {
        this.currentRequestKey = storedRequestKey;
      }

      // Last search params (her zaman yükle - refresh için lazım)
      const storedParams = sessionStorage.getItem(this.STORAGE_KEYS.LAST_SEARCH_PARAMS);
      if (storedParams) {
        this.lastSearchParams = JSON.parse(storedParams);
      }

      // User criteria (her zaman yükle)
      const storedCriteria = sessionStorage.getItem(this.STORAGE_KEYS.USER_CRITERIA);
      if (storedCriteria) {
        this.userSearchCriteria = JSON.parse(storedCriteria);
      }

      // Pagination yükle
      if (isDataFresh) {
        const storedPagination = sessionStorage.getItem(this.STORAGE_KEYS.PAGINATION);
        if (storedPagination) {
          const pagination = JSON.parse(storedPagination);
          this.currentPageNumber = pagination.pageNumber || 1;
          this.totalCount = pagination.totalCount || 0;
          this.pageSize = pagination.pageSize || 10;
        }

        const storedPassengerKeys = sessionStorage.getItem(this.STORAGE_KEYS.PASSENGER_KEYS);
        if (storedPassengerKeys) {
          this.passengerKeys = JSON.parse(storedPassengerKeys);
        }
      }

    } catch (error) {
      // storage load error - silent
    }
  }

  /**
  /**
   * SessionStorage'a verileri kaydeder
   */
  private saveToStorage(): void {
    try {
      // Timestamp kaydet
      sessionStorage.setItem(this.STORAGE_KEYS.TIMESTAMP, Date.now().toString());

      // Search results — sadece hafif özet bilgi sakla (fiyat güncellemesi BehaviorSubject'ten gelir)
      // Tam response sessionStorage'a yazılmaz (performans)

      // Request key
      if (this.currentRequestKey) {
        sessionStorage.setItem(this.STORAGE_KEYS.REQUEST_KEY, this.currentRequestKey);
      }

      // Last search params
      if (this.lastSearchParams) {
        sessionStorage.setItem(this.STORAGE_KEYS.LAST_SEARCH_PARAMS, JSON.stringify(this.lastSearchParams));
      }

      // Pagination
      sessionStorage.setItem(this.STORAGE_KEYS.PAGINATION, JSON.stringify({
        pageNumber: this.currentPageNumber,
        totalCount: this.totalCount,
        pageSize: this.pageSize
      }));

      // User criteria
      if (this.userSearchCriteria) {
        sessionStorage.setItem(this.STORAGE_KEYS.USER_CRITERIA, JSON.stringify(this.userSearchCriteria));
      }

      // Passenger keys
      if (this.passengerKeys.length > 0) {
        sessionStorage.setItem(this.STORAGE_KEYS.PASSENGER_KEYS, JSON.stringify(this.passengerKeys));
      }
    } catch (error) {
      // storage save error - silent
    }
  }

  /**
   * SessionStorage'ı temizler
   */
  private clearStorage(): void {
    Object.values(this.STORAGE_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });
  }

  setSearchResults(results: SearchResponse | null, preserveMinNightInfo: boolean = false) {
    // ⭐ KRİTİK: Önce eski passenger key'leri temizle, sonra yeni response'tan ata
    this.passengerKeys = [];

    if (results?.items?.length) {
      for (const item of results.items) {
        if (item.rooms?.length) {
          for (const room of item.rooms) {
            if (room.passengerKeys?.length) {
              this.passengerKeys = [...room.passengerKeys];
              break;
            }
          }
          if (this.passengerKeys.length > 0) break;
        }
      }
    }

    // RequestKey'i sakla (pagination için)
    if (results?.requestKey) {
      this.currentRequestKey = results.requestKey;
    }
    
    // Pagination bilgilerini güncelle
    if (results) {
      this.currentPageNumber = results.pageNumber || 1;
      this.totalCount = results.totalCount || 0;
      this.pageSize = results.pageSize || 10;
    }
    
    // ⭐ Minimum gece kısıtlaması kontrolü (preserveMinNightInfo false ise güncelle)
    if (!preserveMinNightInfo && results) {
      const altRoomGroup = (results as any).alternativeRoomGroup
        || (results as any).alternativeRoomGroups
        || (results as any).alternativeRoomGroupRoomGroups;

      if (altRoomGroup && altRoomGroup.length > 0) {
        this.alternativeRoomGroupData = altRoomGroup;
        const firstAlt = altRoomGroup[0];
        this.minNightRestrictionInfo = {
          hasRestriction: true,
          minNight: firstAlt.roomGroup?.minNight || 2,
          message: firstAlt.reasons?.[0]?.text
            || firstAlt.roomGroup?.messages?.[0]?.text
            || `Bu otel minimum ${firstAlt.roomGroup?.minNight || 2} gece konaklama gerektirmektedir.`
        };
      } else if (!preserveMinNightInfo && results.items && results.items.length > 0) {
        this.alternativeRoomGroupData = null;
        this.minNightRestrictionInfo = { hasRestriction: false, minNight: 0, message: '' };
      }
    }
    
    this.searchResults.next(results);
    
    // SessionStorage'a kaydet
    this.saveToStorage();
  }

  getPassengerKeys(): string[] {
    return [...this.passengerKeys]; // Copy döndür
  }

  /**
   * Passenger key'leri dışarıdan günceller (hotel-detail addon response'larından gelen güncel key'ler için)
   */
  updatePassengerKeys(keys: string[]): void {
    if (keys?.length) {
      this.passengerKeys = [...keys];
      this.saveToStorage();
    }
  }

  /**
   * Yeni arama öncesi eski passenger state'ini temizler
   */
  clearPassengerState(): void {
    this.passengerKeys = [];
    this.addonData = null;
    this.saveToStorage();
  }

  /**
   * ⭐ Minimum gece kısıtlaması bilgisini döndürür
   */
  getMinNightRestrictionInfo(): { hasRestriction: boolean; minNight: number; message: string } {
    return { ...this.minNightRestrictionInfo };
  }

  /**
   * ⭐ alternativeRoomGroup verisini döndürür
   */
  getAlternativeRoomGroupData(): any[] | null {
    return this.alternativeRoomGroupData;
  }

  /**
   * ⭐ Minimum gece kısıtlaması bilgisini manuel olarak set eder
   */
  setMinNightRestrictionInfo(info: { hasRestriction: boolean; minNight: number; message: string }): void {
    this.minNightRestrictionInfo = { ...info };
  }

  /**
   * RequestKey'i döndürür
   */
  getRequestKey(): string | null {
    return this.currentRequestKey;
  }

  /**
   * Mevcut pagination bilgilerini döndürür
   */
  getPaginationInfo(): { pageNumber: number; totalCount: number; pageSize: number; totalPages: number } {
    const totalPages = Math.ceil(this.totalCount / this.pageSize);
    return {
      pageNumber: this.currentPageNumber,
      totalCount: this.totalCount,
      pageSize: this.pageSize,
      totalPages
    };
  }

  /**
   * Son arama parametrelerini döndürür
   */
  getLastSearchParams(): SearchRequest | null {
    return this.lastSearchParams;
  }

  /**
   * Kullanıcı arama kriterlerini saklar (form verileri)
   * @param criteria Kullanıcının form'a girdiği arama kriterleri
   */
  setUserSearchCriteria(criteria: UserSearchCriteria): void {
    this.userSearchCriteria = { ...criteria };
    this.saveToStorage();
  }

  /**
   * Kullanıcı arama kriterlerini döndürür
   * @returns UserSearchCriteria | null
   */
  getUserSearchCriteria(): UserSearchCriteria | null {
    return this.userSearchCriteria;
  }

  setAddonData(data: { addons: any[]; basePrice: any; totalPrice: any; productKey: string; traceId: string; passengerKeys: string[] }): void {
    this.addonData = { ...data };
  }

  getAddonData(): { addons: any[]; basePrice: any; totalPrice: any; productKey: string; traceId: string; passengerKeys: string[] } | null {
    return this.addonData;
  }

  setBookingSummary(data: { hotelName: string; hotelImage: string; hotelStars: number; roomType: string; mealType: string; checkIn: string; checkOut: string; adultCount: number; childCount: number; roomCount: number }): void {
    this.bookingSummary = { ...data };
  }

  getBookingSummary(): { hotelName: string; hotelImage: string; hotelStars: number; roomType: string; mealType: string; checkIn: string; checkOut: string; adultCount: number; childCount: number; roomCount: number } | null {
    return this.bookingSummary;
  }

  /**
   * İlk arama yapar (tüm kriterler ile)
   * @param searchParams Arama kriterleri
   * @returns Observable<SearchResponse>
   */
  searchHotels(searchParams: SearchRequest): Observable<SearchResponse> {
    const cacheKey = JSON.stringify(searchParams);
    const cached = this.searchCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < this.SEARCH_CACHE_TTL) {
      return cached.obs;
    }

    this.lastSearchParams = { ...searchParams };
    this.currentRequestKey = null;
    this._isSearching.next(true);
    this.saveToStorage();

    const obs = this.http.post<SearchResponse>(`${this.apiUrl}/Search`, searchParams, {
      withCredentials: true
    }).pipe(
      timeout(120000),
      tap(response => {
        if (response) {
          this.setSearchResults(response);
          if (response.traceId) {
            try { sessionStorage.setItem('lastTraceId', response.traceId); } catch {}
          }
        }
      }),
      finalize(() => this._isSearching.next(false)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.searchCache.set(cacheKey, { obs, ts: Date.now() });
    return obs;
  }

  /**
   * Sayfalama ile arama yapar (RequestKey kullanarak)
   * @param pageNumber Sayfa numarası
   * @param pageSize Sayfa boyutu (opsiyonel)
   * @returns Observable<SearchResponse>
   */
  searchWithPagination(pageNumber: number, pageSize?: number): Observable<SearchResponse> {
    if (!this.currentRequestKey) {
      throw new Error('RequestKey bulunamadı. Önce ilk aramayı yapmalısınız.');
    }

    const paginationRequest: SearchRequest = {
      requestKey: this.currentRequestKey,
      pageNumber: pageNumber,
      pageSize: pageSize || this.pageSize
    };

    return this.http.post<SearchResponse>(`${this.apiUrl}/Search`, paginationRequest, {
      withCredentials: true
    }).pipe(
      timeout(120000)
    );
  }

  /**
   * Sonraki sayfaya git
   * @returns Observable<SearchResponse>
   */
  nextPage(): Observable<SearchResponse> {
    const nextPageNumber = this.currentPageNumber + 1;
    const totalPages = Math.ceil(this.totalCount / this.pageSize);
    
    if (nextPageNumber > totalPages) {
      throw new Error('Son sayfadasınız.');
    }
    
    return this.searchWithPagination(nextPageNumber);
  }

  /**
   * Önceki sayfaya git
   * @returns Observable<SearchResponse>
   */
  previousPage(): Observable<SearchResponse> {
    const prevPageNumber = this.currentPageNumber - 1;
    
    if (prevPageNumber < 1) {
      throw new Error('İlk sayfadasınız.');
    }
    
    return this.searchWithPagination(prevPageNumber);
  }

  /**
   * Belirli bir sayfaya git
   * @param pageNumber Sayfa numarası
   * @returns Observable<SearchResponse>
   */
  goToPage(pageNumber: number): Observable<SearchResponse> {
    const totalPages = Math.ceil(this.totalCount / this.pageSize);
    
    if (pageNumber < 1 || pageNumber > totalPages) {
      throw new Error(`Geçersiz sayfa numarası. Sayfa 1 ile ${totalPages} arasında olmalıdır.`);
    }
    
    return this.searchWithPagination(pageNumber);
  }

  handleSearchResponse(response: SearchResponse): void {
    if (response.success) {
      // setSearchResults artık searchHotels() içindeki tap()'te çağrılıyor
      // Her durumda hotel-list'e git (alternatif oda varsa orada uyarı gösterilir)
      this.router.navigate(['/hotel-list']);
    }
  }

  /**
   * Otel bölgesine göre maksimum taksit sayısını döndürür
   * Kıbrıs: 3, Yurtiçi: 9, Yurtdışı: 0 (taksit yok)
   */
  getMaxInstallmentCount(): number {
    const locationType = this.userSearchCriteria?.selectedLocation?.type?.toLowerCase();

    if (locationType === 'cyprus') {
      return 3; // Kıbrıs otelleri: max 3 taksit
    }

    // Yurtdışı oteller: taksit yok
    // country tipi ve Türkiye dışı ise yurtdışı sayılır
    const countryCode = this.lastSearchParams?.countryCode;
    if (countryCode && countryCode !== 'TR') {
      return 0; // Yurtdışı: taksit yok
    }

    return 9; // Yurtiçi: max 9 taksit
  }

  /**
   * Arama state'ini sıfırlar (yeni arama için)
   */
  resetSearch(): void {
    this.currentRequestKey = null;
    this.lastSearchParams = null;
    this.currentPageNumber = 1;
    this.totalCount = 0;
    this.userSearchCriteria = null;
    this.passengerKeys = [];
    this._isSearching.next(false);
    this.searchResults.next(null);
    this.clearStorage();
  }

  /**
   * Mevcut search session'ının geçerli olup olmadığını kontrol eder
   * @returns Observable<boolean>
   */
  checkSearchSession(): Observable<any> {
    return this.http.get(`${this.apiUrl}/CheckSession`, {
      withCredentials: true
    });
  }

  /**
   * Son search parametrelerini kullanarak yeniden search yapar
   * @param searchParams Son kullanılan search parametreleri
   * @returns Observable<any>
   */
  refreshSearchSession(searchParams: SearchRequest): Observable<SearchResponse> {
    return this.searchHotels(searchParams);
  }

  /**
   * Cookie'den değer alır
   * @param name Cookie adı
   * @returns string | null
   */
  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null; // Server-side rendering kontrolü
    }

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }
} 